import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import {
    IOwnershipUser,
    ITransferOwnershipAvailableContent,
} from '@fi-app/other-settings/transfer-ownership/fi-transfer-ownership';
import { ObjectTypes } from '@fi-shared/types';
import { FiTransferOwnershipHelperService } from '@fi-app/other-settings/transfer-ownership/fi-transfer-ownership-helper.service';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { FiTransferOwnershipService } from '@fi-app/other-settings/transfer-ownership/fi-transfer-ownership.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { fiDebounce, isDefined } from '@fi-shared/utils';

@Component({
    selector: 'fi-transfer-ownership-select-content-card',
    templateUrl: './fi-transfer-ownership-select-content-card.component.html',
    styleUrl: './fi-transfer-ownership-select-content-card.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-transfer-ownership-select-content-card' },
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiTransferOwnershipSelectContentCardComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiTransferOwnershipSelectContentCardComponent,
        },
    ],
})
export class FiTransferOwnershipSelectContentCardComponent
    extends ControlValueAccessorValidatorBase<ObjectTypes[]>
    implements OnInit, OnChanges, OnDestroy
{
    @Input({ required: true }) sourceUser: IOwnershipUser;
    @Input({ required: true }) targetUser: IOwnershipUser;

    @Input() isCompleted: boolean;
    availableContents$ = new BehaviorSubject<ITransferOwnershipAvailableContent[]>([]);

    constructor(
        injector: Injector,
        private fiTransferOwnershipHelperService: FiTransferOwnershipHelperService,
        private fiTransferOwnershipService: FiTransferOwnershipService,
    ) {
        super(injector);
    }

    ngOnInit() {}

    ngOnChanges(changes: SimpleChanges): void {
        let change = changes.sourceUser;
        if (change) {
            this.updateAvailableContent();
        }
        change = changes.targetUser;
        if (change) {
            this.updateAvailableContent();
        }
    }

    private isContentTypesTransferDisabled(): boolean {
        return !isDefined(this.sourceUser) || !isDefined(this.targetUser);
    }

    private updateAvailableContent() {
        this.updateAvailableContentDebounced();
    }

    private updateAvailableContentDebounced = fiDebounce(() => {
        if (this.isContentTypesTransferDisabled()) {
            this.setDefaultContents();
            return;
        }

        this.fiTransferOwnershipService
            .getAvailableObjectTypes(this.sourceUser.Id, this.targetUser.Id)
            .then((availableObjectTypes: ObjectTypes[]) => this.setAvailableContents(availableObjectTypes));
    });

    private setAvailableContents(availableObjectTypes: ObjectTypes[]) {
        this.availableContents$.next(
            this.fiTransferOwnershipHelperService.getAvailableTransferOwnershipContents(availableObjectTypes),
        );
        this.doPropagateChanges();
    }

    private setDefaultContents() {
        this.availableContents$.next(this.fiTransferOwnershipHelperService.getTransferOwnershipDefaultContent());
        this.doPropagateChanges();
    }

    doPropagateChanges() {
        this.ngModel = this.availableContents$.value
            .filter((availableContent: ITransferOwnershipAvailableContent) => availableContent.isSelected)
            .map((availableContent: ITransferOwnershipAvailableContent) => availableContent.objectType);
        super.doPropagateChanges();
    }

    getContentTitle(objectType: ObjectTypes): string {
        return this.fiTransferOwnershipHelperService.getContentTitle(objectType, true, true);
    }

    doValidate(c: AbstractControl): ValidationErrors {
        return {
            required: !this.ngModel?.length,
        };
    }

    ngOnDestroy$ = new Subject<void>();

    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
